/**
 * Public FHIR client surface (PROD-642 Sprint 2.2).
 *
 * Single entry point for the rest of the app to obtain a `FhirClient`.
 * The mode toggle reads `import.meta.env.VITE_FHIR_MODE` (`live` | `demo`)
 * with `demo` as the default. Live Mode requires the four
 * `VITE_AIDBOX_*` env vars to be present; absent any of them, the
 * factory falls back to the Demo Mode client and emits a console warning.
 *
 * The factory is memoized so the rest of the app sees one shared client
 * instance per process. Tests can override via `__setFhirClientForTests`.
 *
 * HC1 reproducibility: the Demo Mode default seed is byte-deterministic.
 * HC9 strict registry: AuditEvent writes are gathered into a `MemorySink`
 * exposed via `getAuditEvents()`.
 *
 * HC5: zero em / en dashes anywhere.
 */
import { LiveAidboxFhirClient, type FhirClient } from './client';
import { DemoFhirClient, type DemoFhirClientOptions } from './demo-client';
import { seedDemoFhirClient } from './seed';

export type {
  FhirClient,
  LiveAidboxClientOptions,
  RetryOptions,
} from './client';
export type { DemoFhirClientOptions } from './demo-client';
export { LiveAidboxFhirClient } from './client';
export { DemoFhirClient, MemorySink } from './demo-client';
export { seedDemoFhirClient } from './seed';
export {
  EtagConflictError,
  FhirAuthError,
  FhirClientError,
  FhirNotFoundError,
  FhirTransportError,
  FhirValidationError,
  TenantMismatchError,
} from './errors';
export {
  RISK_TIER_EXTENSION_URL,
  TENANT_EXTENSION_URL,
} from './types';
export {
  assertTenantMatches,
  readRiskTier,
  readTenantId,
  withRiskTier,
  withTenantId,
} from './extensions';
export type * from './types';

export type FhirMode = 'live' | 'demo';

/**
 * Read the FHIR mode from Vite env. Defaults to `demo` when the env var
 * is absent, malformed, or set to anything other than `live`.
 */
export function resolveFhirMode(): FhirMode {
  try {
    const env = (import.meta as unknown as { env?: Record<string, unknown> })
      .env;
    const raw = env?.VITE_FHIR_MODE;
    return raw === 'live' ? 'live' : 'demo';
  } catch {
    return 'demo';
  }
}

interface ResolvedLiveEnv {
  baseUrl: string;
  tokenUrl: string;
  clientId: string;
  clientSecret: string;
}

/**
 * Resolve the four live-mode env vars. Returns null when any of them
 * are missing, prompting the factory to fall back to the demo client.
 */
function resolveLiveEnv(): ResolvedLiveEnv | null {
  try {
    const env = (import.meta as unknown as { env?: Record<string, unknown> })
      .env;
    const baseUrl = env?.VITE_AIDBOX_BASE_URL;
    const tokenUrl = env?.VITE_AIDBOX_TOKEN_URL;
    const clientId = env?.VITE_AIDBOX_CLIENT_ID;
    const clientSecret = env?.VITE_AIDBOX_CLIENT_SECRET;
    if (
      typeof baseUrl !== 'string' ||
      typeof clientId !== 'string' ||
      typeof clientSecret !== 'string'
    ) {
      return null;
    }
    // Token URL defaults to the Aidbox `/auth/token` endpoint relative to
    // the base host when not explicitly overridden.
    const resolvedTokenUrl =
      typeof tokenUrl === 'string' && tokenUrl.length > 0
        ? tokenUrl
        : deriveTokenUrl(baseUrl);
    return {
      baseUrl,
      tokenUrl: resolvedTokenUrl,
      clientId,
      clientSecret,
    };
  } catch {
    return null;
  }
}

/** Derive the token URL from a FHIR base URL. */
function deriveTokenUrl(baseUrl: string): string {
  // Strip a trailing `/fhir` if present, then append `/auth/token`.
  const trimmed = baseUrl.replace(/\/$/, '').replace(/\/fhir$/, '');
  return `${trimmed}/auth/token`;
}

/** Memoized singleton. */
let cachedClient: FhirClient | null = null;

/**
 * Detect a Vite production-build context. Vite inlines `VITE_*` env
 * vars at build time, so any `VITE_AIDBOX_CLIENT_SECRET` baked into a
 * production bundle ships to every user that loads the bundle. This is
 * acceptable for in-house local supervisor smoke (per repo CLAUDE.md
 * "Browser-secret caveat") but NOT acceptable for an externally
 * deployed prototype: a server-side token broker is required first.
 * The guard below emits a loud console warning when Live Mode is
 * selected in a production bundle so this trade-off is observable.
 */
function isProductionBundle(): boolean {
  try {
    const env = (import.meta as unknown as { env?: Record<string, unknown> })
      .env;
    return env?.PROD === true;
  } catch {
    return false;
  }
}

/**
 * Return the process-wide FHIR client. Reads the mode toggle on first
 * call, then memoizes. Subsequent calls return the same instance until
 * `__resetFhirClientForTests()` is invoked.
 */
export function getFhirClient(): FhirClient {
  if (cachedClient) return cachedClient;
  const mode = resolveFhirMode();
  if (mode === 'live') {
    const env = resolveLiveEnv();
    if (!env) {
      console.warn(
        '[fhir] VITE_FHIR_MODE=live but VITE_AIDBOX_* env vars missing; falling back to demo client',
      );
      cachedClient = createDemoClient();
      return cachedClient;
    }
    if (isProductionBundle()) {
      console.error(
        '[fhir] DEPLOYMENT HAZARD: VITE_FHIR_MODE=live in a PROD bundle. ' +
          'VITE_AIDBOX_CLIENT_SECRET is now inlined into client-side JS and ' +
          'will ship to every user. Acceptable only for in-house local ' +
          'supervisor smoke; production deployment requires a server-side ' +
          'token broker per repo CLAUDE.md "Browser-secret caveat". ' +
          'Continuing with Live client for now.',
      );
    }
    cachedClient = new LiveAidboxFhirClient({
      baseUrl: env.baseUrl,
      tokenUrl: env.tokenUrl,
      clientId: env.clientId,
      clientSecret: env.clientSecret,
    });
    return cachedClient;
  }
  cachedClient = createDemoClient();
  return cachedClient;
}

/**
 * Build a fresh demo client with the deterministic seed pre-loaded.
 * Exported so `pathway.store` can construct an isolated client when it
 * needs deterministic state per test.
 */
export function createDemoClient(
  options: DemoFhirClientOptions = {},
): DemoFhirClient {
  const seed = options.seed ?? seedDemoFhirClient;
  return new DemoFhirClient({ ...options, seed });
}

/** Reset the memoized client. Tests + Demo Mode reset call this. */
export function __resetFhirClientForTests(): void {
  cachedClient = null;
}

/** Inject a custom client for tests. */
export function __setFhirClientForTests(client: FhirClient | null): void {
  cachedClient = client;
}
